import { ReportChartConfiguration, ReportChartFilterItemTypes, ReportChartMeasureType } from '@vision/ui/interfaces';
import { array, object, string, TestContext } from 'yup';
import { BarChart1CompareRenderer, BarChart1MeasureRenderer } from '../../components';
import {
  DefaultReportChartCompareSchema,
  DefaultReportChartThemeFont,
  DefaultReportChartThemeSeries,
  extendDefaultChartValidationSchema,
} from '../default-chart.config';
import { BarChart1Filtres } from './bar-chart1.filtre';
import { BarChart1Measures } from './bar-chart1.measures';

const VALIDATION_SCHEMA = {
  selectedCompareCriteria: object().shape({
    ...DefaultReportChartCompareSchema,
  }),
  selectedCompare: array()
    .of(string())
    .when('selectedMeasure', {
      is: (value: string) => !['matrix-question-csat-overview'].includes(value),
      then: (field) => field.min(1, 'validation:rules.generic.required'),
    }),
  selectedFilters: object().shape({
    selectedQuestionIds: array()
      .of(string())
      .test(
        'selectedCompareCriteria',
        'validation:rules.generic.required',
        function (value: any, context: TestContext) {
          const selectedMeasure = context.from[1]?.value['selectedMeasure'];
          if (['matrix-question-csat-overview'].includes(selectedMeasure) && value.length === 0) {
            return false;
          }
          return true;
        },
      ),
  }),
};

/*
We can make the shcema and measures values later.
Because we are not selecting 'matrix-question-csat-overview' compare
we are only selecting measure. That's why we pass the selectedCompare values.
*/
export const BarChart1DefaultConfiguration: ReportChartConfiguration = {
  supportsLegendSettings: true,
  validationSchema: extendDefaultChartValidationSchema({
    params: VALIDATION_SCHEMA,
    withCompareCriteria: false,
    withFeedbackBasedQuestionBased: true,
  }),
  measures: BarChart1Measures,
  compareRendererComponent: BarChart1CompareRenderer,
  measureRendererComponent: BarChart1MeasureRenderer,
  compareMultiple: true,
  hideCompareCondition: (values) => ['matrix-question-csat-overview'].includes(values.selectedMeasure),
  customize: {
    active: (values) => {
      // matrix-question-csat-overview seçildiğinde compare seçilmiyor.
      if (['matrix-question-csat-overview'].includes(values.selectedMeasure)) {
        return values.selectedFilters.selectedQuestionIds.length > 0;
      }

      return values.selectedMeasure && values.selectedCompare.length > 0;
    },
    transactionalDate: {
      active: false,
    },
    feedbackCount: {
      active: false,
    },
    limits: {
      upperLimitActive: false,
      upperLimit: 0,
      lowerLimitActive: false,
      lowerLimit: 0,
    },
    sortResults: {
      limit: 0,
      order: null,
    },
  },
  filters: (measureType: ReportChartMeasureType) => {
    for (const item of BarChart1Filtres) {
      if (item.measures.includes(measureType)) {
        return item.filterItems;
      }
    }
    return ReportChartFilterItemTypes;
  },
  defaultTheme: {
    active: (values) => {
      // matrix-question-csat-overview seçildiğinde compare seçilmiyor.
      if (['matrix-question-csat-overview'].includes(values.selectedMeasure)) {
        return values.selectedFilters.selectedQuestionIds.length > 0;
      }

      return values.selectedMeasure && values.selectedCompare.length > 0;
    },
    title: {
      ...DefaultReportChartThemeFont,
      label: '',
    },
    xAxis: {
      ...DefaultReportChartThemeFont,
      minMaxValue: {
        minValue: null,
        maxValue: null,
      },
    },
    yAxis: {
      ...DefaultReportChartThemeFont,
    },
    dataLabels: {
      ...DefaultReportChartThemeFont,
      active: false,
      alignment: 'horizontal',
      displayFormat: 'auto',
      roundingMethod: 'default',
      labelDisplayFormat: 'number',
      position: 'outside',
    },
    series: {
      ...DefaultReportChartThemeSeries,
    },
  },
};
